import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { Project, ProjectMeta } from '../../projects/project.model';
import { ProjectHelper } from '../../projects/project.helper';
import { ConnectProjectVersionControlDialogService } from '../../projects/connect-project-version-control/connect-project-version-control-dialog.service';
import _ from 'lodash';
import { PublishProjectChangesetHelper } from '../../projects/publish-project/publish-project-changeset.helper';
import { ProjectService } from '../../projects/project.service';
import { Observable, of } from 'rxjs';
import { Revision } from '../../version-control/revision/revision.model';
import { catchError, map } from 'rxjs/operators';

@Component({
    selector: 'adm4-project-information',
    template: `
    <div class="section-title">
      <span>Project information</span>
      <span ngbTooltip="You don't have permission to delete this project." [disableTooltip]="hasProjectModifyAccess" placement="left">
        <button type="button" class="admn4-button-ellipse-red" [class.cursor-disabled]="!hasProjectModifyAccess"
          [disabled]="!hasProjectModifyAccess" (click)="deleteProject.emit()"
        >Delete project</button>
      </span>
    </div>
    <div class='project-information'>
      <table class='settings-info-table'>
        <tr>
          <td id="project-info-key">
            <div class="key">Project key</div>
            <div><a [routerLink]="['/projects', project.projectKey]">{{project.projectKey}}</a></div>
          </td>
          @if (project.repository) {
            <td id="project-info-repo">
              <div class="key">Repository</div>
              <span>{{ project.repository }}</span>
            </td>
          }
          @if (project.path) {
            <td id="project-info-path">
              <div class="key">Directory in repository</div>
              <span>{{ project.path }}</span>
            </td>
          }
        </tr>
        <tr>
          <td id="project-info-tenant">
            <div class="key">Tenant key</div>
            <span>{{ project.tenantKey }}</span>
          </td>
          @if (project.branch) {
            <td id="project-info-branch">
              <div class="key">Branch</div>
              <span>{{ project.branch }}</span>
            </td>
          }
          @if (commitId) {
            <td id="project-info-commit-id">
              <div class='key'>Commit</div>
              <span [ngbTooltip]='projectPublishStatus' [disableTooltip]="!projectPublishStatus" class='commit-info'>
                <span>{{ commitId }}</span>
                @if (projectHasChanges) {
                  <em class="fa fa-info-circle help-icon" aria-hidden="true"></em>
                }
              </span>
            </td>
          }
          @if ((tags$ | async); as tags) {
            @if (tags.length > 0) {
              <td id="project-info-tags">
                <div class='key'>Tags</div>
                <span>{{ tags.join(', ') }}</span>
              </td>
            }
          }
          @if (project.repositoryUsername) {
            <td id="project-info-repo-username">
              <div class="key">Repository username</div>
              <span>{{ project.repositoryUsername }}</span>
            </td> 
          }
          @if (project.repositoryPasswordSecretId) {
            <td id="project-info-repo-password-secret-id">
              <div class="key">Repository password secret-ID</div>
              <span>{{ project.repositoryPasswordSecretId }}</span>
            </td> 
          }
        </tr>
      </table>
      @if (!isVersionedProject) {
        <div class='project-actions'>
          <span ngbTooltip="You don't have permission to change Git settings on this project." [disableTooltip]="hasProjectModifyAccess" placement="left">
            <button type="button" class="admn4-button-ellipse-blue" [class.cursor-disabled]="!hasProjectModifyAccess"
              [disabled]="!hasProjectModifyAccess" (click)="openConnectProjectToVersionControlDialog()">
              <span class="menu-item-text">Connect to Git</span>
            </button>
          </span>
        </div>
      }
    </div>
    `,
    styleUrls: ['../../common/styles/component-specific/settings-info-table.scss', './project-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProjectInformationComponent implements OnChanges {
  private connectProjectToVersionControlDialogService = inject(ConnectProjectVersionControlDialogService);
  private projectService = inject(ProjectService);


  @Input() project: Project;
  @Input() projectMeta: ProjectMeta | null;
  @Input() hasProjectModifyAccess: boolean = false;

  @Output() deleteProject: EventEmitter<void> = new EventEmitter();

  tags$: Observable<string[]>;

  constructor() {
    this.tags$ = of([]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['project'] || changes['projectMeta'])) {
      if (this.isVersionedProject && this.project.projectKey && this.commitId) {
        this.tags$ = this.projectService.getProjectRevision(this.project.projectKey, this.commitId).pipe(
          map((revision: Revision) => revision?.tags ?? []),
          catchError(() => of([]))
        );
      } else {
        this.tags$ = of([]);
      }
    }
  }

  openConnectProjectToVersionControlDialog(): void {
    this.connectProjectToVersionControlDialogService.openConnectProjectToVersionControlDialog(this.project);
  }

  get isVersionedProject(): boolean {
    return ProjectHelper.isVersionedProject(this.project);
  }

  get projectHasChanges(): boolean {
    return !_.isNil(this.projectMeta) && PublishProjectChangesetHelper.hasLocalChanges(this.projectMeta);
  }

  get projectPublishStatus(): string {
    if (_.isNil(this.projectMeta)) {
      return '';
    } else if (this.projectHasChanges) {
      return 'There are local changes pending to be published on this project';
    } else {
      return 'There are no local changes pending to be published on this project';
    }
  }

  get commitId(): string | undefined {
    return _.isNil(this.projectMeta) ? this.project.commitId : this.projectMeta.localHead;
  }
}
